import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { Property } from "../types";
import { buildAuctionSheet } from "../lib/auctionSheet";
import { resolveDisplayAddress } from "../lib/dataQuality";
import { formatCurrency, formatDateTime } from "../lib/format";

// Printable auction-week sheet (#3211): the next 7 days of scheduled sales by
// day, with the debt or opening-bid figure, the sale time and place when the
// notice states them, and when the upset period closes. Opened from the list
// header; App.css's @media print block hides everything but this sheet.

interface Props {
  /** The rows the list is showing (already filtered), so the sheet follows the filters. */
  properties: Property[];
  watchedIds: Set<string>;
  countyName?: string;
  onClose: () => void;
  /** Called when the user prints, for analytics. */
  onPrint?: (rows: number) => void;
}

const DAYS = 7;

function todayKey(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function dayLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

const AuctionSheet: React.FC<Props> = ({ properties, watchedIds, countyName, onClose, onPrint }) => {
  const days = useMemo(
    () => buildAuctionSheet(properties, todayKey(), DAYS, watchedIds),
    [properties, watchedIds]
  );
  const total = days.reduce((n, d) => n + d.rows.length, 0);

  // Portaled to <body> so the print stylesheet can show it alone, whatever the
  // list pane around the button is doing (scroll containers, transforms).
  return createPortal(
    <div className="auction-sheet-backdrop" role="dialog" aria-modal="true" aria-label="Auction week sheet">
      <div className="auction-sheet">
        <div className="auction-sheet-actions">
          <button
            type="button"
            className="lv-chip"
            onClick={() => {
              onPrint?.(total);
              window.print();
            }}
            disabled={total === 0}
          >
            Print
          </button>
          <button type="button" className="lv-chip" onClick={onClose}>
            Close
          </button>
        </div>

        <h2 className="auction-sheet-title">
          Auctions, next {DAYS} days{countyName ? ` · ${countyName}` : ""}
        </h2>
        <p className="auction-sheet-note">
          {total === 0
            ? "No scheduled sales in the next week for the properties you're viewing."
            : `${total} sale${total === 1 ? "" : "s"}. A sale is not final until its upset period closes. Amounts marked ~ are estimates, not a published bid.`}
        </p>

        {days.map((day) => (
          <section key={day.date} className="auction-sheet-day">
            <h3>{dayLabel(day.date)}</h3>
            <table>
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Time / place</th>
                  <th className="num">Owed / bid</th>
                  <th>Upset closes</th>
                </tr>
              </thead>
              <tbody>
                {day.rows.map((r) => {
                  const addr = resolveDisplayAddress(r.property);
                  return (
                    <tr key={r.property.id} className={r.watched ? "auction-sheet-row--watched" : undefined}>
                      <td>
                        {r.watched && <span aria-label="On your watchlist">★ </span>}
                        <strong>{addr.text || "Address not published"}</strong>
                        <div className="auction-sheet-sub">
                          {[r.property.city, r.property.countyId].filter(Boolean).join(" · ")}
                          {r.property.parcelId ? ` · ${r.property.parcelId}` : ""}
                        </div>
                      </td>
                      <td>{[r.time, r.location].filter(Boolean).join(" · ") || "Not stated"}</td>
                      <td className="num">
                        {r.amount != null ? `${r.amountIsEstimate ? "~" : ""}${formatCurrency(r.amount)}` : "Not published"}
                      </td>
                      <td>{r.upsetCloseAt ? formatDateTime(r.upsetCloseAt) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}

        <div className="print-footer" aria-hidden="true">
          Printed {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} from
          parcelyapp.com
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AuctionSheet;
